import PropTypes from 'prop-types'
import React from 'react'

import Tooltip from './components/Tooltip'
import { TooltipButtonInner, TooltipButtonWrapper } from './styles'

/**
 * Tooltip button that calls and render tooltip using portal
 *
 * It closes on scroll, resize or second click everywhere/mouse leave (depends on `withHover` prop)
 *
 * @returns { TooltipButton } TooltipButton — Component of TooltipButton
 */
const TooltipButton = ({
  children,
  content,
  position,
  withHover,
  close,
  deltaArrow,
  topIndentNode,
  leftIndentNode,
  indents,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const originRef = React.useRef(null)

  const handleClose = React.useCallback(
    (e, type) => {
      if (isOpen && (!type || close[type])) {
        setIsOpen(false)
      }
    },
    [isOpen, close],
  )

  const handleScroll = React.useCallback(e => handleClose(e, 'scroll'), [
    handleClose,
  ])
  const handleResize = React.useCallback(e => handleClose(e, 'resize'), [
    handleClose,
  ])
  const handleBodyClick = React.useCallback(e => handleClose(e, 'click'), [
    handleClose,
  ])
  const handleCrossClick = React.useCallback(e => handleClose(e, 'cross'), [
    handleClose,
  ])
  const handleMouseLeave = React.useCallback(
    e => handleClose(e, 'mouseLeave'),
    [handleClose],
  )

  const handleOpen = React.useCallback(() => {
    if (!isOpen) {
      setIsOpen(true)
    }
  }, [isOpen])

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    document.body.addEventListener('click', handleBodyClick, true)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.body.removeEventListener('click', handleBodyClick, true)
    }
  }, [isOpen, close, handleScroll, handleResize, handleBodyClick])

  return (
    <TooltipButtonWrapper>
      <TooltipButtonInner
        type="button"
        ref={originRef}
        onClick={isOpen ? null : handleOpen}
        onMouseEnter={withHover ? handleOpen : null}
        onMouseLeave={withHover ? handleMouseLeave : null}
      >
        {children}
      </TooltipButtonInner>
      <Tooltip
        isOpen={isOpen}
        originRef={originRef}
        position={position}
        topIndentNode={topIndentNode}
        leftIndentNode={leftIndentNode}
        deltaArrow={deltaArrow}
        closeConfiguration={close}
        onCrossClick={handleCrossClick}
        withCloseIcon={close.cross}
        indents={indents}
      >
        {content}
      </Tooltip>
    </TooltipButtonWrapper>
  )
}

TooltipButton.propTypes = {
  /** Tooltips button's content node */
  children: PropTypes.node.isRequired,

  /** Tooltips's content node */
  content: PropTypes.node.isRequired,

  /** Configuration for close options */
  close: PropTypes.shape({
    click: PropTypes.bool,
    cross: PropTypes.bool,
    resize: PropTypes.bool,
    scroll: PropTypes.bool,
    mouseLeave: PropTypes.bool,
  }),

  /** Delta for custom move tooltip's arrow */
  deltaArrow: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),

  /** Position of tooltip */
  position: PropTypes.oneOf(['top', 'bottom', 'right', 'left']),

  /** Node that has to be above a tooltip instance
   * so we use it to set an indent from the beginning of a page
   * to tooltip */
  topIndentNode: PropTypes.object,

  /** Node that we use to set an indent from the left side of a page
   * to tooltip */
  leftIndentNode: PropTypes.object,

  indents: PropTypes.object,

  /** Opens on hover if true, opens on click if false  */
  withHover: PropTypes.bool,
}

TooltipButton.defaultProps = {
  position: 'top',
  withHover: false,
  close: {
    click: true,
    scroll: true,
    resize: true,
    mouseLeave: true,
    cross: false,
  },
}

export default TooltipButton
